import React, { useEffect, useState } from 'react';
import '/site/parts/calendar/calendar.scss';
import {
    Calendar,
    Typography,
    Col,
    Select,
    Row,
    Card,
    Skeleton,
    Modal,
    ConfigProvider,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/ru';
import { capitalizeFirstLetter, getLangValueForCalendar, localize } from 'mixins/helpers';
import CalendarLeftArrowIcon from '/react4xp/mixins/ui/icons/calendar-left-arrow-icon.svg';
import CalendarRightArrowIcon from '/react4xp/mixins/ui/icons/calendar-right-arrow-icon.svg';
import ArrowRightIcon from '../../../icons/arrow-right-icon.svg';
import Button from 'mixins/ui/button/button';
import Background from '../../../assets/images/bg_calendar.svg';
import {
    getAllMessageEventsByDate,
    getMessageEventsInfoForPeriod,
} from '/react4xp/parts/calendar/actions';

import ruRu from 'antd/lib/locale/ru_RU';
import enEn from 'antd/lib/locale/en_US';
import kkKk from 'antd/lib/locale/kk_KZ';

const dateFormat = 'DD.MM.YYYY';
const dateFormat2 = 'YYYY-MM-DD';

const CalendarBlock = (props) => {
    const [currDate, setCurrDate] = useState<Dayjs>(dayjs());
    const [currMonth, setCurrMonth] = useState<string>();
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [daysWithEvents, setDaysWithEvents] = useState({});
    const [selectedDate, setSelectedDate] = useState(null);
    const [dateData, setDateData] = useState({});

    // Apply the locale to Day.js based on language prop
    useEffect(() => {
        const locale = getLangValueForCalendar(props.language);
        dayjs.locale(locale);
    }, [props.language]);

    const loadData = async () => {
        const currentMonth = dayjs(currDate).format('YYYY-MM');
        setCurrMonth(currentMonth);

        setLoading(true);

        try {
            const cacheDates = dateData;
            const taxCalendarDate = dayjs(currDate).format(dateFormat2);

            if (!cacheDates[taxCalendarDate]) {
                const eventsData = await getAllMessageEventsByDate({ taxCalendarDate });

                if (eventsData) {
                    cacheDates[taxCalendarDate] = eventsData.data;

                    setDateData(cacheDates);
                    setEvents(eventsData.data);
                }
            } else {
                setEvents(cacheDates[taxCalendarDate]);
            }

            if (currentMonth !== currMonth) {
                const eventsInfoData = await getMessageEventsInfoForPeriod(currentMonth);

                if (eventsInfoData) {
                    setDaysWithEvents(getDaysWithEvents(eventsInfoData.data));
                }
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, [currDate]);

    const getDaysWithEvents = (eventData) => {
        const daysWithEvents = {};

        if (eventData && Array.isArray(eventData)) {
            eventData.forEach((event) => {
                const eventDate = dayjs(event.taxCalendarDate, dateFormat2);
                const dayKey = eventDate.format(dateFormat);
                daysWithEvents[dayKey] = true;
            });
        }

        return daysWithEvents;
    };

    const onPrevDayClick = () => {
        const prevDayDate = currDate.subtract(1, 'day');
        setCurrDate(prevDayDate);
        setSelectedDate(prevDayDate);
    };

    const onNextDayClick = () => {
        const nextDayDate = currDate.add(1, 'day');
        setCurrDate(nextDayDate);
        setSelectedDate(nextDayDate);
    };

    const showModal = (event) => {
        setSelectedEvent(event);
        setModalOpen(true);
    };

    const handleCancel = () => {
        setModalOpen(false);
        setSelectedEvent(null);
    };

    const hasEventsForDay = (day) => {
        const dayFormatted = day.format(dateFormat2);
        return events.some((event) => event.taxCalendarDate === dayFormatted);
    };

    function getLocalizedTitle(event, language) {
        if (event && event.title && language) {
            switch (String(language).toLowerCase()) {
                case 'ru_RU':
                case 'ru':
                    return event.title.nameRu;
                case 'kk':
                case 'kz':
                case 'kk_KZ':
                    return event.title.nameKk || event.title.nameRu;
                case 'qq':
                case 'en_150':
                    return event.title.nameQq || event.title.nameRu;
                case 'en_US':
                case 'en':
                    return event.title.nameEn || event.title.nameRu;
                default:
                    return event.title.nameRu;
            }
        } else {
            return '';
        }
    }

    function getLocalizedContent(event, language) {
        if (event && event.content) {
            switch (language) {
                case 'ru_RU':
                case 'ru':
                    return event.content.nameRu;
                case 'kk':
                case 'kz':
                case 'kk_KZ':
                    return event.content.nameKk || event.content.nameRu;
                case 'qq':
                case 'en_150':
                    return event.content.nameQq || event.content.nameRu;
                case 'en_US':
                case 'en':
                    return event.content.nameEn || event.content.nameRu;
                default:
                    return event.content.nameRu;
            }
        } else {
            return '';
        }
    }

    const getLocaleLanguage = () => {
        switch (props.language) {
            case 'ru_RU':
            case 'ru':
                return ruRu;
            case 'kk':
            case 'kz':
            case 'kk_KZ':
                return kkKk;
            case 'qq':
            case 'en_150':
                return kkKk;
            case 'en_US':
            case 'en':
                return enEn;
            default:
                return ruRu;
        }
    };

    // Function to fetch correct month names based on the current locale
    const getLocalizedMonths = () => {
        const locale = dayjs.locale();
        const testDate = dayjs().set('month', 0); // Start from January
        return Array.from({ length: 12 }, (_, i) =>
            testDate.month(i).locale(locale).format('MMMM')
        ); // Full month names
    };

    return (
        <ConfigProvider locale={getLocaleLanguage()}>
            <div
                className="calendar"
                id="calendar"
            >
                <div className="iKTLFT">
                    <div className="fNOngg">
                        <Calendar
                            fullscreen={false}
                            dateFullCellRender={(date) => {
                                const dayKey = date.format(dateFormat);
                                const hasEvents = daysWithEvents[dayKey];
                                const isCurrentDay = date.isSame(dayjs(), 'day');
                                const isSelectedDay = selectedDate
                                    ? date.isSame(selectedDate, 'day')
                                    : false;
                                return (
                                    <div
                                        className={`calendar-cell ${hasEvents ? 'event_day' : ''} ${
                                            isCurrentDay ? 'current_day' : ''
                                        } ${isSelectedDay ? 'selected_day' : ''}`}
                                        onClick={() => setSelectedDate(date)}
                                    >
                                        {date.format('D')}
                                    </div>
                                );
                            }}
                            headerRender={({ value, type, onChange }) => {
                                const months = getLocalizedMonths(); // Retrieve full localized month names
                                const start = 0;
                                const end = 12;
                                const monthOptions = [];

                                for (let i = start; i < end; i++) {
                                    monthOptions.push(
                                        <Select.Option
                                            key={i}
                                            value={i}
                                            className="month-item"
                                        >
                                            {capitalizeFirstLetter(months[i])}{' '}
                                            {/* Display full localized month names */}
                                        </Select.Option>
                                    );
                                }

                                const year = value.year();
                                const month = value.month();
                                const options = [];
                                for (let i = year - 10; i < year + 10; i += 1) {
                                    options.push(
                                        <Select.Option
                                            key={i}
                                            value={i}
                                            className="year-item"
                                        >
                                            {i}
                                        </Select.Option>
                                    );
                                }
                                return (
                                    <div className="main__div">
                                        <div className="background_logo">
                                            <Background className="background" />
                                        </div>
                                        <Row
                                            gutter={8}
                                            justify="space-around"
                                            align="middle"
                                        >
                                            <Col>
                                                <span className="calendar__title">
                                                    {localize('calendar.title', props.translations)}
                                                </span>
                                            </Col>
                                            <Col>
                                                <Select
                                                    size="small"
                                                    dropdownMatchSelectWidth={false}
                                                    className="my-year-select"
                                                    value={year}
                                                    onChange={(newYear) => {
                                                        const now = value.clone().year(newYear);
                                                        onChange(now);
                                                    }}
                                                >
                                                    {options}
                                                </Select>
                                                <Select
                                                    size="small"
                                                    dropdownMatchSelectWidth={false}
                                                    value={month}
                                                    onChange={(newMonth) => {
                                                        const now = value.clone().month(newMonth);
                                                        onChange(now);
                                                    }}
                                                >
                                                    {monthOptions}
                                                </Select>
                                            </Col>
                                        </Row>
                                    </div>
                                );
                            }}
                            value={currDate}
                            onChange={setCurrDate}
                        />
                        <div className="hxzWXX">
                            <div className="fMMLZL">
                                <button
                                    className="ezrfDf hzhaS"
                                    onClick={onPrevDayClick}
                                >
                                    <CalendarLeftArrowIcon />
                                </button>
                                <button
                                    className="ezrfDf hzhaS"
                                    onClick={onNextDayClick}
                                >
                                    <CalendarRightArrowIcon />
                                </button>
                                <span className="epzbLs">{currDate.format(dateFormat)}</span>
                            </div>
                            <div className="card__container">
                                <Skeleton loading={loading}>
                                    <Row
                                        gutter={16}
                                        style={{
                                            minWidth:
                                                370 * events.length + 16 * (events.length - 1),
                                            flexWrap: 'nowrap',
                                        }}
                                    >
                                        {events.map((event) => (
                                            <Col
                                                className="col"
                                                span={8}
                                                key={event.id}
                                            >
                                                <Card
                                                    title={
                                                        <div
                                                            dangerouslySetInnerHTML={{
                                                                __html: getLocalizedTitle(
                                                                    event,
                                                                    props.language
                                                                ),
                                                            }}
                                                        />
                                                    }
                                                    bordered={false}
                                                    style={{ width: 370, height: 265 }}
                                                >
                                                    <div
                                                        className="content"
                                                        dangerouslySetInnerHTML={{
                                                            __html: getLocalizedContent(
                                                                event,
                                                                props.language
                                                            ),
                                                        }}
                                                    />
                                                    <a
                                                        className="link__more"
                                                        onClick={() => showModal(event)}
                                                    >
                                                        {localize(
                                                            'more-detailed',
                                                            props.translations
                                                        )}
                                                        <ArrowRightIcon
                                                            style={{ marginLeft: '1px' }}
                                                        />
                                                    </a>
                                                </Card>
                                            </Col>
                                        ))}
                                    </Row>
                                </Skeleton>
                                <Modal
                                    title={
                                        selectedEvent ? (
                                            <div
                                                className="div__text"
                                                dangerouslySetInnerHTML={{
                                                    __html: getLocalizedTitle(
                                                        selectedEvent,
                                                        props.language
                                                    ),
                                                }}
                                            />
                                        ) : null
                                    }
                                    open={modalOpen}
                                    onCancel={handleCancel}
                                    style={{ minWidth: '800px' }}
                                    footer={
                                        <Button
                                            className="modal__button-calendar"
                                            color="gold"
                                            onClick={handleCancel}
                                            style={{
                                                alignItems: 'center',
                                            }}
                                        >
                                            {localize('button.close', props.translations)}
                                        </Button>
                                    }
                                >
                                    {selectedEvent && (
                                        <>
                                            <div
                                                className="main__text"
                                                dangerouslySetInnerHTML={{
                                                    __html: getLocalizedContent(
                                                        selectedEvent,
                                                        props.language
                                                    ),
                                                }}
                                            />
                                            <div className="modal__date">
                                                <p className="modal__date-text">
                                                    {localize('data', props.translations)}:{''}
                                                </p>
                                                <div
                                                    dangerouslySetInnerHTML={{
                                                        __html: selectedEvent.taxCalendarDate,
                                                    }}
                                                />
                                            </div>
                                        </>
                                    )}
                                </Modal>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </ConfigProvider>
    );
};

export default CalendarBlock;
